import { darken } from "polished"
import { formatDecimalForDisplay } from "./utils/formatting-utils"

const environmentDetails = {
  name: process.env.REACT_APP_ENVIRONMENT_TITLE,
  ttDomain: process.env.REACT_APP_TALENTTOOLBOX_DOMAIN,
  scheme: process.env.REACT_APP_TALENTTOOLBOX_SCHEME,
}

const identityServerDetails = {
  clientId: "REPSSO",
  authAddress: process.env.REACT_APP_IDENTITY_AUTH_ADDRESS,
  requestScopes: "openid+TTReportingApi",
  reportingAppUrl: process.env.REACT_APP_IDENTITY_APP_URL,
}

export const ENVIRONMENT = {
  getTalentToolboxUrl: (subdomain, path = "/") => {
    if (!subdomain) return "#"
    return `${environmentDetails.scheme}://${subdomain}${environmentDetails.ttDomain}${path}`
  },
  getApiRootUrl: () => {
    return process.env.REACT_APP_API_URL // "https://ttfilesuat.blob.core.windows.net/reporting/i18n/en-GB"
  },
  getLoginPath(nonce) {
    if (process.env.REACT_APP_SHOW_LOGIN_LINK && process.env.REACT_APP_SHOW_LOGIN_LINK === "true") {
      return (
        `${identityServerDetails.authAddress}/connect/authorize?` +
        `client_id=${identityServerDetails.clientId}` +
        `&scope=${identityServerDetails.requestScopes}&response_type=id_token+code&` +
        `redirect_uri=${encodeURIComponent(identityServerDetails.reportingAppUrl)}` +
        `&grant_type=hybrid&nonce=${nonce}`
      )
    }
    return null
  },
  useDummyData: () => {
    if (process.env.REACT_APP_USE_DUMMY_DATA && process.env.REACT_APP_USE_DUMMY_DATA === "true") {
      return true
    }
    return false
  },
}

export const USERMANAGERCONFIG = {
  authority: `${identityServerDetails.authAddress}`,
  redirect_uri: `${identityServerDetails.reportingAppUrl}/auth/callback`,
  post_logout_redirect_uri: `${identityServerDetails.reportingAppUrl}/index.html`,
  silent_redirect_uri: `${identityServerDetails.reportingAppUrl}/auth/sign-in-silent`,
}

// ***** Routes *****
export const ROUTES = {
  dashboards: {
    prefix: "/dashboard/",
    peopleBalanceSheet: "/dashboard/people-balance-sheet",
    employeeEngagement: "/dashboard/employee-engagement",
    intelAndInsights: "/dashboard/intel-and-insights",
    performanceAndProgression: "/dashboard/performance-and-progression",
    activityAndGeneralStats: "/dashboard/activity-and-general-stats",
    customReport: "/dashboard/custom-reports",
  },
  auth: {
    signInSilent: "/auth/sign-in-silent",
    callback: "/auth/callback",
    preLogin: "/auth/pre-login",
    signInRequired: "/auth/sign-in-required",
  },
  talentToolboxUrls: {
    terms: "/text/terms-and-conditions",
    privacy: "/text/privacy-policy",
    cookies: "/text/cookies",
    contactUs: "/text/contact-us",
  },
}

// ***** Theming *****
const themeColours = {
  primaryColor: "#513b8b",
}

// The theme object needs some transforms (e.g. to darken hover styles based on the primary colour)
// Todo: Replace this - it won't be a constant, we need to get the client's theme
export const DEFAULTTHEME = {
  ...themeColours,
  primaryColorHover: darken(0.2, themeColours.primaryColor),
  navBackgroundColor: darken(0.2, themeColours.primaryColor),
}

const lineChartColourArray = ["#788EC7", "#3ca67e", "#7b6bb5", "#b7ede8"]

const chartColourArray = [
  // 1
  ["#1d8399"],
  // 2
  ["#7e8caf", "#7B6BB5"],
  // 3
  ["#1d8399", "#88A19E", "#A18F9E"],
  // 4
  ["#7e8caf", "#7B6BB5", "#1d8399", "#a5a9a9"],
  // 5
  ["#7e8caf", "#788EC7", "#e4d7c9", "#cacccb", "#A18F9E"],
  // 6
  ["#cacccb", "#A18F9E", "#3CA67E", "#E8B385", "#B7EDE8", "#88A19E"],
  // 7
  ["#a5a9a9", "#7B6BB5", "#1d8399", "#7e8caf", "#788EC7", "#88A19E", "#cacccb"],
  // 8
  ["#7e8caf", "#7B6BB5", "#1d8399", "#a5a9a9", "#788EC7", "#e4d7c9", "#cacccb", "#A18F9E"],
  // 9
  [
    "#7e8caf",
    "#7B6BB5",
    "#1d8399",
    "#a5a9a9",
    "#788EC7",
    "#e4d7c9",
    "#cacccb",
    "#A18F9E",
    "#3CA67E",
  ],
  [
    "#7e8caf",
    "#7B6BB5",
    "#1d8399",
    "#a5a9a9",
    "#788EC7",
    "#e4d7c9",
    "#cacccb",
    "#A18F9E",
    "#3CA67E",
    "#E8B385",
  ],
  [
    "#ace9b6",
    "#beebc5",
    "#cfedd3",
    "#e0efe2",
    "#f1f1f1",
    "#e7d9ec",
    "#dcc2e8",
    "#d1abe3",
    "#c694de",
  ],
  [
    "#7e8caf",
    "#7B6BB5",
    "#1d8399",
    "#a5a9a9",
    "#788EC7",
    "#e4d7c9",
    "#cacccb",
    "#A18F9E",
    "#3CA67E",
    "#E8B385",
    "#88A19E",
  ],
  [
    "#7e8caf",
    "#7B6BB5",
    "#1d8399",
    "#a5a9a9",
    "#788EC7",
    "#e4d7c9",
    "#cacccb",
    "#A18F9E",
    "#3CA67E",
    "#E8B385",
    "#B7EDE8",
    "#88A19E",
  ],
]

export const CHARTS = {
  animationEnabled: true,
  colours: chartColourArray[12],
  trafficLightColours: {
    green: "#82C6BC",
    amber: "#F4DF9B",
    red: "#C2778C",
  },
  exportEnabled: true,
  exportButtons: [
    {
      textKey: "downloadPNG",
      onclick() {
        this.exportChart()
      },
    },
    {
      textKey: "downloadJPEG",
      onclick() {
        this.exportChart({
          type: "image/jpeg",
        })
      },
    },
    {
      textKey: "downloadPDF",
      onclick() {
        this.exportChart({
          type: "application/pdf",
        })
      },
    },
    {
      textKey: "downloadSVG",
      onclick() {
        this.exportChart({
          type: "image/svg+xml",
        })
      },
    },
  ],
  exportChartOptions: {
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          formatter() {
            return formatDecimalForDisplay(this.y)
          },
          shadow: false,
          color: "#333333",
          style: {
            fontWeight: "normal",
            fontSize: "8px",
            textOutline: false,
          },
        },
      },
    },
    chart: {
      spacingTop: 50,
    },
  },
  getRandomColour: (key) => {
    let colourSet = chartColourArray[6]
    colourSet = [...colourSet, ...colourSet.reverse()]

    // Make a number based off of the title translation key
    const makeLongNumber = (key.length * Math.PI).toString()
    const array = makeLongNumber.split("")

    // Remove decimal place from string
    const index = array.indexOf(".")
    if (index > -1) {
      array.splice(index, 1)
    }
    const numberArray = array.map(Number)
    // Use last number in chain as color index
    // Likely to vary a lot but will always be the same for wigets with same title
    return colourSet[numberArray[numberArray.length - 1]]
  },
  getLineColours: () => {
    return lineChartColourArray
  },
  getAreaColours: (numberOfColours) => {
    let index = numberOfColours

    if (numberOfColours > chartColourArray.length) index = chartColourArray.length

    return chartColourArray[index - 1]
  },
  getColours: (numberOfColours) => {
    // Gets a bunch of complimenting colours with more contrast when only
    // a small number of colours are required. Advice taken from:
    // https://blog.graphiq.com/finding-the-right-color-palettes-for-data-visualizations-fcd4e707a283
    let index = numberOfColours

    if (numberOfColours > chartColourArray.length) index = chartColourArray.length

    return chartColourArray[index - 1]
  },
  standardHeight: 350,
}

// ***** Internationalisation *****
export const DATEFORMAT = "DD/MM/YYYY"
export const DATETIMEFORMAT = "DD/MM/YYYY HH:mm:ss"

export const ENDPOINTS = {
  Export: {
    AverageLengthOfService: "/reporting/average-length-of-service-gauge/export",
    BehaviourAverage: "/reporting/overall-behaviour-average-statistics/export",
    BehaviourBreakdown: "/reporting/behaviour-breakdown-bar-statistics/export",
    ChallengesFaced: "/reporting/challenges-faced-statistics/export",
    CoffeeChatsCompleted: "/reporting/coffee-chats-completed/export",
    CustomNetPromoter: "/reporting/custom-net-promoter/export",
    DirectReports: "/reporting/direct-report-statistics/export",
    DoBest: "/reporting/do-best-statistics/export",
    EngagementAverageScore: "/reporting/engagement-average-score/export",
    EngagementCombinedAverages: "/reporting/engagement-score-over-time/export",
    FeatureUsage: "/reporting/feature-usage/export",
    Freedom: "/reporting/freedom-statistics/export",
    GoalScore: "/reporting/goal-score-statistics/export",
    GoalStatusBreakdown: "/reporting/goal-status-breakdown-statistics/export",
    HappyScale: "/reporting/happy-scale-statistics/export",
    Journeys: "/reporting/journey/export",
    LabourTurnover: "/reporting/labour-turnover/export",
    LAndDCompleted: "/reporting/l-and-d-completed/export",
    LAndDStats: "/reporting/l-and-d-statistics/export",
    LeaversInFirst100Days: "/reporting/leavers-in-first-100/export",
    LengthOfService: "/reporting/length-of-service-pie-statistics/export",
    Logins: "/reporting/logins/export",
    MeetingNeeds: "/reporting/meeting-needs-statistics/export",
    NeedsImproving: "/reporting/needs-improving-statistics/export",
    NineBoxGridLarge: "/reporting/succession-planning/export",
    NetPromoterScoreStats: "/reporting/net-promoter-score-statistics/export",
    PerformanceScore: "/reporting/performance/export",
    PerformanceComparison: "/reporting/performance-comparison/export",
    PulseChecks: "/reporting/pulse-check/export",
    ReadyToMoveOn: "/reporting/ready-to-move-on/export",
    ReviewAgreement: "/reporting/review-agreement-statistics/export",
    ReviewsCompleted: "/reporting/reviews-completed/export",
    RiskOfLeavingBar: "/reporting/risk-of-leaving-bar-statistics/export",
    RiskOfLeavingPie: "/reporting/risk-of-leaving-pie-statistics/export",
    SuccessionPlanning: "/reporting/succession-planning/export",
    TemperatureCheckResponseRate: "/reporting/temp-check-response-rate-statistics/export",
    TemperatureCheckScores: "/reporting/temp-check-scores/export",
    TotalEmployees: "/reporting/total-employee-statistics/export",
    TopUsedFeatures: "/reporting/feature-usage/export",
    ValueAlignment: "/reporting/value-alignment-statistics/export",
    PerformanceComparisonChart: "/reporting/performance-comparison/export",
  },
}
export const CUSTOMREPORTENDPOINTS = {
  Download: "/custom-reports/download",
}

export const SORTCOLUMNS = {
  EmployeeAsc: 0,
  EmployeeDesc: 1,
  PotentialAsc: 2,
  PotentialDesc: 3,
  PerformanceAsc: 4,
  PerformanceDesc: 5,
  EngagementAsc: 6,
  EngagementDesc: 7,
}



// WEBPACK FOOTER //
// ./src/constants.js